import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Circle, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, shortDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusPill } from "@/components/StatusPill";

export function TasksPanel({ caseId, isStaff = false }) {
  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");

  const load = useCallback(() => {
    api.get(`/cases/${caseId}/tasks`).then(({ data }) => setTasks(data)).catch(() => {});
  }, [caseId]);

  useEffect(load, [load]);

  const toggle = async (task) => {
    try {
      await api.patch(`/tasks/${task.id}`);
      toast.success(task.status === "completed" ? "Task reopened" : "Task marked complete");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await api.post(`/cases/${caseId}/tasks`, { title, due_date: due ? new Date(due).toISOString() : null });
      setTitle("");
      setDue("");
      toast.success("Task assigned to client");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="tasks-panel" className="space-y-6">
      {isStaff ? (
        <form onSubmit={create} className="glass rounded-xl p-4 flex flex-col sm:flex-row gap-3" data-testid="task-form">
          <Input
            data-testid="task-title-input"
            placeholder="Task for the client"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="bg-white/5 border-white/15"
          />
          <Input
            data-testid="task-due-input"
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            className="bg-white/5 border-white/15 sm:w-44"
          />
          <Button data-testid="task-create-btn" className="rounded-full bg-gold text-navy hover:bg-gold-dark shrink-0">
            <Plus className="h-4 w-4 mr-1.5" /> Assign
          </Button>
        </form>
      ) : null}

      <div className="space-y-3">
        {tasks.map((t) => (
          <div key={t.id} className="glass rounded-xl px-5 py-4 flex items-start gap-4" data-testid="task-row">
            <button
              data-testid="task-toggle-btn"
              onClick={() => toggle(t)}
              className="mt-0.5 transition-transform hover:scale-110"
              aria-label="Toggle task"
            >
              {t.status === "completed" ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              ) : (
                <Circle className="h-5 w-5 text-gold" />
              )}
            </button>
            <div className="flex-1">
              <p className={`text-sm ${t.status === "completed" ? "text-[#6B7280] line-through" : "text-[#F3F4F6]"}`}>{t.title}</p>
              {t.description ? <p className="text-xs text-[#9CA3AF] mt-1">{t.description}</p> : null}
              <p className="text-xs text-[#6B7280] mt-1.5">Due {shortDate(t.due_date)}</p>
            </div>
            <StatusPill value={t.status} testId="task-status-pill" />
          </div>
        ))}
        {!tasks.length ? <p className="text-sm text-[#9CA3AF]">No outstanding actions.</p> : null}
      </div>
    </div>
  );
}
